import Joi from 'joi';
import { TECHNOLOGIES } from './BulkAction/TechnologiesInput';
import type { FilterMeta } from './types';

export const LINKS = [
  {
    title: 'How to manage your SIMs?',
    url: 'https://help.onomondo.com/en/collections/323614-sim-management-guides',
  },
];

export const INITIAL_COLUMNS = [
  'id',
  'label',
  'type',
  'ipv4',
  'networkWhitelist',
  'usage',
  'tags',
  'activated',
  'connector',
  'pricePlan',
  'techFee',
];

export const CSV_FILTER_RULES: Record<string, Joi.StringSchema<string>> = {
  simId: Joi.string()
    .length(9)
    .pattern(/^[0-9]+$/, { name: 'numbers' })
    .messages({
      'string.length': 'SIM ID must be exactly 9 characters long.',
      'string.pattern.name': 'SIM ID must contain only numbers',
    }),
  iccid: Joi.string()
    .length(20)
    .pattern(/^[0-9]+$/, { name: 'numbers' })
    .messages({
      'string.length': 'ICCID must be exactly 20 characters long.',
      'string.pattern.name': 'ICCID must contain only numbers',
    }),
  imsi: Joi.string()
    .length(15)
    .pattern(/^[0-9]+$/, { name: 'numbers' })
    .messages({
      'string.length': 'IMSI must be exactly 15 characters long.',
      'string.pattern.name': 'IMSI must contain only numbers',
    }),
  ipv4: Joi.string()
    .ip({ version: ['ipv4'] })
    .messages({
      'string.ipVersion': 'IPv4 must be a valid IP address',
    }),
};

export const COLUMNS_TO_FILTERS_MAPPING = {
  networkMcc: 'mcc',
  networkCountryCode: 'countryCode',
  tags: 'tagTypes',
};

export const KEY_MAP = {
  simId: 'sim_id',
  networkWhitelist: 'network_whitelist',
  type: 'type',
  countryCode: 'country_code',
  networkName: 'network_name',
  tagTypes: 'tag_types',
  networkCountryCode: 'network_country_code',
  lastCameOnlineAt: 'last_came_online_at',
};

export const ONLINE_STATUS_ITEMS = [
  { label: 'Online', value: 'true' },
  { label: 'Offline', value: 'false' },
];

export const ACTIVE_STATUS_ITEMS = [
  { label: 'Active', value: 'true' },
  { label: 'Deactivated', value: 'false' },
];

export const TAG_TYPE_ITEMS = [
  { label: 'View Only', value: 'is_read' },
  { label: 'Edit & View', value: 'is_write' },
];

export const SIM_TYPE_ITEMS = [
  { label: 'SoftSIM', value: 'softsim' },
  { label: 'Physical', value: 'physical' },
  { label: 'eSIM', value: 'euicc_smdpp' },
];

export const FILTERS_DATA: Record<string, FilterMeta> = {
  connector: {
    label: 'Connector',
    type: 'combobox',
  },
  tagTypes: {
    toApiKey: 'tag_types',
    label: 'Tag Type',
    type: 'combobox',
    items: TAG_TYPE_ITEMS,
    multiple: true,
    showSearch: false,
  },
  online: {
    label: 'Online status',
    type: 'combobox',
    items: ONLINE_STATUS_ITEMS,
    showSearch: false,
  },
  simId: {
    toApiKey: 'sim_id',
    label: 'SIM ID',
    type: 'csv',
    itemLength: 10,
  },
  label: {
    label: 'Label',
    type: 'csv',
    itemLength: 20,
  },
  ipv4: {
    label: 'IPv4',
    type: 'csv',
    itemLength: 15,
  },
  iccid: {
    label: 'ICCID',
    type: 'csv',
    itemLength: 20,
  },
  activated: {
    label: 'Active',
    type: 'combobox',
    items: ACTIVE_STATUS_ITEMS,
    showSearch: false,
    mixpanelType: 'boolean',
  },
  tags: {
    label: 'Tags',
    type: 'combobox',
    multiple: true,
    maxVisibleItems: 1,
  },
  networkWhitelist: {
    toApiKey: 'network_whitelist',
    label: 'Network list',
    type: 'combobox',
  },
  type: {
    label: 'Type',
    type: 'combobox',
    items: SIM_TYPE_ITEMS,
    showSearch: false,
  },
  mcc: {
    label: 'MCC',
    type: 'combobox',
  },
  countryCode: {
    toApiKey: 'country_code',
    label: 'Current country',
    type: 'combobox',
  },
  networkName: {
    toApiKey: 'network_name',
    label: 'Current network',
    type: 'combobox',
  },
  technologies: {
    label: 'Technologies',
    type: 'combobox',
    items: TECHNOLOGIES,
    multiple: true,
    showSearch: false,
  },
  imsi: {
    label: 'IMSI',
    type: 'csv',
    itemLength: 15,
  },
};

export const COLUMN_TYPE_TO_MIXPANEL_TYPE_MAPPING = {
  combobox: 'list',
};
